// Solomon Portal v1 — auth state machine + useAuth() hook.
//
// State model (ADR-018, replacing the earlier flat-string shape from
// ADR-014):
//   - status: 'loading' | 'unauthenticated' | 'unprovisioned' | 'authenticated'
//             | { kind: 'error'; reason: AuthErrorReason; details?: string }
//   - 'authenticated' implies a valid session AND a corresponding portal_users.users row.
//   - 'unprovisioned' means the magic link worked, but no portal_users row matches —
//     the user must contact support@solomonadvising.com.
//   - { kind: 'error', reason } captures every other failure mode, with a
//     discriminator on `reason` so downstream UIs can route to the
//     appropriate dedicated recovery screen instead of leaving users on a
//     bare `#error=...` URL fragment.

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { logger } from './logger';
import { writeAuditEvent } from './audit';
import type { Database } from '../types/database.generated';

export type ClientUser = Database['portal_users']['Tables']['users']['Row'];

export type AuthErrorReason =
  | 'link_expired'
  | 'link_invalid'
  | 'link_consumed'
  | 'rls_failure'
  | 'network_timeout'
  | 'pkce_exchange_failed'
  | 'unknown';

export interface AuthErrorState {
  kind: 'error';
  reason: AuthErrorReason;
  details?: string;
}

export type AuthStatus =
  | 'loading'
  | 'unauthenticated'
  | 'unprovisioned'
  | 'authenticated'
  | AuthErrorState;

export interface AuthState {
  status: AuthStatus;
  session: Session | null;
  clientUser: ClientUser | null;
}

/**
 * Type guard for the discriminated error shape.
 */
export function isAuthError(status: AuthStatus): status is AuthErrorState {
  return typeof status === 'object' && status !== null && status.kind === 'error';
}

// Hard timeout on the auth-bootstrap RLS query. If `loadClientUser` doesn't
// resolve in this window we abort the request and surface the recovery UI.
// See ADR-014.
const LOAD_CLIENT_USER_TIMEOUT_MS = 8000;

const initialState: AuthState = {
  status: 'loading',
  session: null,
  clientUser: null,
};

const AuthContext = createContext<AuthState>(initialState);

type LoadClientUserError = 'rls' | 'timeout' | 'network' | null;

interface LoadClientUserResult {
  user: ClientUser | null;
  error: LoadClientUserError;
}

async function loadClientUser(
  authUserId: string,
  signal?: AbortSignal,
): Promise<LoadClientUserResult> {
  try {
    // RLS scopes this to the caller's own row when authenticated; deleted_at is filtered.
    const query = supabase
      .schema('portal_users')
      .from('users')
      .select('*')
      .eq('auth_user_id', authUserId);
    if (signal) {
      query.abortSignal(signal);
    }
    const { data, error } = await query.maybeSingle();
    if (error) {
      logger.error({
        source: 'auth.loadClientUser',
        message: 'RLS query failed',
        payload: { code: error.code, hint: error.hint ?? null },
      });
      return { user: null, error: 'rls' };
    }
    return { user: data, error: null };
  } catch (err: unknown) {
    const name =
      err && typeof err === 'object' && 'name' in err
        ? String(err.name)
        : '';
    if (name === 'AbortError') {
      logger.warn({
        source: 'auth.loadClientUser',
        message: 'RLS query aborted (timeout)',
        payload: { timeoutMs: LOAD_CLIENT_USER_TIMEOUT_MS },
      });
      return { user: null, error: 'timeout' };
    }
    logger.error({
      source: 'auth.loadClientUser',
      message: 'unexpected error',
      payload: { error: String(err) },
    });
    return { user: null, error: 'network' };
  }
}

function loadErrorToReason(err: Exclude<LoadClientUserError, null>): AuthErrorReason {
  switch (err) {
    case 'rls':
      return 'rls_failure';
    case 'timeout':
      return 'network_timeout';
    case 'network':
      return 'network_timeout';
  }
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): ReactNode {
  const [state, setState] = useState<AuthState>(initialState);

  useEffect(() => {
    let mounted = true;

    async function applySession(session: Session | null): Promise<void> {
      if (!session) {
        if (mounted) {
          setState({ status: 'unauthenticated', session: null, clientUser: null });
        }
        return;
      }
      const ctrl = new AbortController();
      const timeoutId = setTimeout(
        () => ctrl.abort(),
        LOAD_CLIENT_USER_TIMEOUT_MS,
      );
      const { user, error } = await loadClientUser(session.user.id, ctrl.signal);
      clearTimeout(timeoutId);
      if (!mounted) return;
      if (user) {
        setState({ status: 'authenticated', session, clientUser: user });
        return;
      }
      if (error) {
        const reason = loadErrorToReason(error);
        // Fire-and-forget audit event; never throws (see audit.ts).
        void writeAuditEvent({
          event_type: `auth.error.${reason}`,
          payload: { source: 'auth.applySession' },
        });
        setState({
          status: { kind: 'error', reason },
          session,
          clientUser: null,
        });
        return;
      }
      // No row, no error — user is signed in but not provisioned in portal_users.
      setState({ status: 'unprovisioned', session, clientUser: null });
    }

    // Initial load.
    void supabase.auth
      .getSession()
      .then(({ data }) => applySession(data.session))
      .catch((err: unknown) => {
        logger.error({
          source: 'auth.getSession',
          message: 'getSession failed',
          payload: { error: String(err) },
        });
        if (mounted) {
          setState({ status: 'unauthenticated', session: null, clientUser: null });
        }
      });

    const { data: subscription } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        void applySession(session);
      },
    );

    return () => {
      mounted = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo(() => state, [state]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  return useContext(AuthContext);
}

export async function signOut(): Promise<void> {
  try {
    await supabase.auth.signOut();
  } finally {
    if (typeof window !== 'undefined') {
      const beacon = (window as { Beacon?: (action: string) => void }).Beacon;
      try {
        beacon?.('destroy');
      } catch (err) {
        logger.warn({
          source: 'auth.signOut',
          message: 'Beacon destroy failed',
          payload: { error: String(err) },
        });
      }
    }
  }
}
